// AC-Infinity-Cloud-Controller-Snapshot fuer /zuhause/aktoren/acinfinity.
//
// Datenfluss (seit 02.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Growbox über das Protokoll“): jeder Controller ist eine Instanz
// growbox.<id>.stand, die Lage des Cloud-Zugangs die Instanz growbox.lage.
// Das Backend (internal/protokoll/growbox) gleicht den Stand des Clients alle
// 2 s ab (der Client pollt die Cloud selbst alle 30 s, Aktionen ändern seinen
// Stand sofort); der Hub verwirft Unverändertes und schickt Deltas. Bis dahin
// holte dieser Store alle 5 s GET /v2-api/acinfinity -- je Tab.
//   - REST (`/v2-api/acinfinity`) beim Start, als Revalidator (Reconnect, Tab
//     kommt zurück), in refreshAcInfinity (nach einer Aktion der Seite: Slider,
//     Schalter, Phase, Aus) und als Neuversuch alle 15 s, solange ein Abruf
//     scheiterte. KEIN Takt mehr.
//   - `growbox.*.stand` und `growbox.lage` (Rate normal): Snapshot + Delta je
//     Instanz. Ein Stand aktualisiert den Controller in-place (Objekt-Identität
//     bleibt), `weg` nimmt ihn heraus. Die Reihenfolge der Cloud steht in
//     `position` des Stands (lib/growbox-protokoll.ts); ein Stand während eines
//     Abrufs liegt über dessen Antwort, ein `weg` hinterlässt einen Grabstein
//     (ProtokollListe, wie bei den Agenten).
//   - Den Snapshot der Seite (`getAcInfinity`) baut lib/growbox-protokoll.ts aus
//     Lage und Controllern in genau der Form der REST-Antwort.
//
// SWR: ja -- Page rendert beim Reload sofort die letzten Sensor-Werte
// + Port-Stati statt erst "Lade...".

import { getJSON } from "./api";
import {
  geraetAusProtokoll,
  growboxIdAusInstanz,
  lageAusProtokoll,
  positionAusStand,
  snapshotAusProtokoll,
  vergleicheGeraete,
  type Lage,
} from "./growbox-protokoll";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { ProtokollListe } from "./protokoll-liste.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { GrowboxLage, GrowboxStand } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";

export interface AcInfinityPort {
  port: number;
  name: string;
  type: string;
  level: number;
  on_level: number;
  off_level: number;
  on: boolean;
  mode: number;
  surplus: number;
}

export interface AcInfinityDevice {
  id: string;
  name: string;
  mac: string;
  online: boolean;
  sensors: { temperature?: number; humidity?: number; vpd?: number };
  ports: AcInfinityPort[];
  current_phase?: string;
}

export interface AcInfinitySnapshot {
  configured: boolean;
  authenticated: boolean;
  email?: string;
  devices: AcInfinityDevice[];
  phases: string[];
  last_poll: string;
}

const STORAGE_KEY = "acinfinity";

// Die Stelle jedes Controllers in der Liste der Cloud: aus dem Stand
// (`position`) bzw. der Reihenfolge der REST-Antwort.
const _position = new Map<string, number>();
const _geraete = new ProtokollListe<AcInfinityDevice>((e) => e.id, vergleicheGeraete((id) => _position.get(id)));
// Die Lage des Zugangs und die laufende Nummer ihres jüngsten Stands (kam er
// während eines Abrufs, liegt er über dessen Antwort).
let _lage = $state<Lage | undefined>(undefined);
let _lageNr = 0;
let _nr = 0;
let _started = false;
let _unsub: Array<() => void> = [];

// Der Snapshot der Seite; vorhanden, sobald die Lage bekannt ist (Ablage,
// Abruf oder Stand). `devices` ist die Liste selbst (in-place aktualisiert).
const _snapshot = $derived.by((): AcInfinitySnapshot | undefined => {
  return _lage ? snapshotAusProtokoll(_lage, _geraete.items) : undefined;
});

// ── Persistenz ───────────────────────────────────────────────────────

// Aus dem Protokoll kommt oft etwas (Messwerte ändern sich mit jedem Abruf der
// Cloud): gespeichert wird gebündelt, höchstens alle 2 s.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    if (_lage) persist(STORAGE_KEY, $state.snapshot(_snapshot));
  }, 2_000);
}

// ── Stände ──────────────────────────────────────────────────────────

function beiStand(instanz: string, daten: Record<string, unknown>): void {
  const id = growboxIdAusInstanz(instanz);
  if (id === undefined) return;
  const st = skaliere<GrowboxStand>("growbox.stand", daten);
  const pos = positionAusStand(st);
  if (pos !== undefined) _position.set(id, pos);
  _geraete.stand(geraetAusProtokoll(id, st));
  // Die Stelle kann sich ändern, ohne dass sich der Controller ändert.
  _geraete.ordne();
  persistSpaeter();
}

function beiWeg(instanz: string): void {
  const id = growboxIdAusInstanz(instanz);
  if (id === undefined) return;
  _position.delete(id);
  _geraete.weg(id);
  persistSpaeter();
}

function beiLage(_instanz: string, daten: Record<string, unknown>): void {
  _lage = lageAusProtokoll(skaliere<GrowboxLage>("growbox.lage", daten));
  _lageNr = ++_nr;
  persistSpaeter();
}

// ── REST ─────────────────────────────────────────────────────────────

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst erst
// der nächste Revalidator.
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

async function laden(): Promise<void> {
  const ab = _geraete.nr;
  const abLage = _nr;
  const lauf = _lauf;
  let snap: AcInfinitySnapshot | undefined;
  try {
    snap = await getJSON<AcInfinitySnapshot>("/v2-api/acinfinity");
  } catch {
    snap = undefined;
  }
  if (lauf !== _lauf) return;
  if (snap) {
    // Die Reihenfolge der Antwort ist die der Cloud; ein Stand, der während des
    // Abrufs kam, kennt seine Stelle selbst.
    snap.devices.forEach((d, i) => {
      if (!_geraete.jungerAls(d.id, ab)) _position.set(d.id, i);
    });
    // Lage: ein Stand während des Abrufs liegt darüber.
    if (_lageNr <= abLage) {
      _lage = {
        configured: snap.configured,
        authenticated: snap.authenticated,
        phases: snap.phases ?? [],
        last_poll: snap.last_poll,
        email: snap.email ?? "",
      };
    }
  }
  // Was das Protokoll während eines gescheiterten Abrufs gebracht hat, gilt
  // trotzdem.
  _geraete.mische(snap ? snap.devices : undefined, ab);
  _geraete.bereit = true;
  persistSpaeter();
  if (!snap && _started && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_started) void laden();
    }, NEUVERSUCH_MS);
  }
}

export function startAcInfinity(): void {
  if (_started) return;
  _started = true;
  _lauf++;
  // SWR: gecachten Snapshot sofort einspielen. Das Protokoll bringt den Live-
  // Stand danach in Sekunden frisch, der Cache deckt nur die First-Paint-Lücke.
  const cached = loadPersisted<AcInfinitySnapshot>(STORAGE_KEY);
  if (cached) {
    cached.devices.forEach((d, i) => _position.set(d.id, i));
    _geraete.items = cached.devices;
    _lage = {
      configured: cached.configured,
      authenticated: cached.authenticated,
      phases: cached.phases ?? [],
      last_poll: cached.last_poll,
      email: cached.email ?? "",
    };
  }
  void laden();
  _unsub.push(
    abonniere("growbox.*.stand", { rate: "normal" }, { stand: beiStand, weg: beiWeg }),
    abonniere("growbox.lage", { rate: "normal" }, { stand: beiLage }),
  );
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
  // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
  // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch.
  _unsub.push(registerRevalidator(laden));
}

export function stopAcInfinity(): void {
  _started = false;
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _geraete.leere();
  _position.clear();
  _lage = undefined;
  _lageNr = 0;
  _nr = 0;
}

export function getAcInfinity(): AcInfinitySnapshot | undefined {
  return _snapshot;
}

/** Holt den Snapshot sofort per REST -- nach einer Aktion, deren Ergebnis die
 *  Seite gleich zeigen soll (das Protokoll braucht bis zu 2 s). */
export function refreshAcInfinity(): Promise<void> {
  // Nach einem Stopp (Seite verlassen, Aktion endet später) nichts mehr füllen.
  if (!_started) return Promise.resolve();
  return laden();
}
